// User-facing settings (architecture §7.4). One `settings` row per key; missing keys use SETTINGS_DEFAULTS.
// Keys are flat dotted strings ("budget.monthly_cap_eur"), the same in the DB, the API and the web cache.
import { z } from 'zod';
import type { ModelCaps, ModelId } from './domain.ts';

export const ModelIdSchema = z.enum(['claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-4-5', 'claude-fable-5-1']);
export const EffortSchema = z.enum(['low', 'medium', 'high', 'xhigh', 'max']);
/** Per model: valid for the model AND every fallback target (§10.5). Frozen per segment (segments.model_caps). */
export const ModelCapsSchema = z.object({ systemMessages: z.boolean(), perMessageEffort: z.boolean() }).strict();
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Uhrzeit im Format HH:MM');

export const SettingsSchema = z.object({
  'profile.text': z.string().max(4000),
  // CR-1 (change-requests.md): seven forms in this order; only the second-person "Eure Lordschaft".
  'address.mode': z.enum(['mix', 'sir', 'stz', 'boss', 'gnaden', 'lord', 'chris']),
  'answer.length': z.enum(['kurz', 'normal', 'ausfuehrlich']),
  'ui.theme': z.enum(['auto', 'hell', 'dunkel']),
  'ui.accent': z.enum(['silber', 'eis']),
  'models.default_agent': ModelIdSchema,
  'models.voice': ModelIdSchema,
  'models.voice_effort': z.enum(['low', 'medium']),     // higher levels would need max_tokens >= 64000 (§10.5)
  'models.utility': ModelIdSchema,
  'budget.monthly_cap_eur': z.number().min(0).max(100_000),
  'budget.warn_ratio': z.number().min(0).max(1),
  'budget.hard_stop': z.boolean(),
  'budget.eur_per_usd_fallback': z.number().positive().max(10),
  'budget.elevenlabs_eur_per_1k_chars': z.number().min(0).max(100).nullable(),
  'voice.default_voice_id': z.string().min(1).max(100).nullable(),
  'voice.tts_model': z.enum(['eleven_flash_v2_5', 'eleven_v4_turbo']),
  'voice.speed': z.number().min(0.7).max(1.2),
  'voice.stability': z.number().min(0).max(1),
  'voice.similarity': z.number().min(0).max(1),
  'voice.auto_read': z.boolean(),
  'voice.speak_new': z.boolean(),
  'voice.wake': z.enum(['aus', 'begruessung', 'briefing']),
  'voice.basis': z.object({ voiceURI: z.string().max(300), voiceName: z.string().max(300), rate: z.number().min(0.1).max(10), pitch: z.number().min(0).max(2) }),
  'tones.normal': z.string().min(1).max(40),
  'tones.urgent': z.string().min(1).max(40),
  'push.normal': z.boolean(),
  'push.dringend': z.boolean(),
  'push.sperrbildschirm': z.enum(['neutral', 'kurz']),
  /** Chris 14.10.2026 (v1): Support answers customer mails by itself (its Gmail trigger job and its hourly run) only when
   *  this is on. Off (the default, also after a seed upgrade): Support reads along, sends, labels and archives nothing. */
  'support.auto_antworten': z.boolean(),
  'calls.enabled': z.boolean(),
  'calls.quiet_hours': z.object({ from: hhmm, to: hhmm }).nullable(),
  'calls.provider': z.enum(['push', 'twilio']),
  'meetings.rounds': z.number().int().min(1).max(3),
  'cockpit.low_stock_threshold': z.number().int().min(0).max(10_000),
  'cockpit.minutes_per_action': z.record(z.string().min(1).max(100), z.number().min(0).max(600)),
  'notion.pages': z.object({ analyst_wissen: z.string(), nachschub: z.string(), produktionskalender: z.string(), infomaterial: z.string() }),
  'notion.intern_heading': z.string().min(3).max(80),
  'shopify.doppelname_hold_hinweis': z.string().min(6).max(80),
  'runtime.compact_at_tokens': z.number().int().min(20_000).max(900_000),
  /** §10.5: written only by the diagnostics run (raise) or a production 400 (lower); never by PATCH /api/settings (400).
   *  Read at segment creation and frozen in segments.model_caps. Exhaustive: one entry per ModelId. */
  'runtime.modelCaps': z.record(ModelIdSchema, ModelCapsSchema),
  /** §10.5: each switch turns a beta header and its request field on or off together; unknown keys → 400. */
  'runtime.features': z.object({ serverFallback: z.boolean(), thinkingUpdates: z.boolean(), blockBinding: z.boolean(), perMessageEffort: z.boolean() }).strict(),
});

export type Settings = z.infer<typeof SettingsSchema>;
export type SettingsKey = keyof Settings;

/** Keys the server writes itself; not editable in the UI, PATCH /api/settings rejects them with 400 (§7.4). */
export const SERVER_ONLY_SETTINGS = ['runtime.modelCaps'] as const satisfies readonly SettingsKey[];
export type ServerOnlySettingsKey = (typeof SERVER_ONLY_SETTINGS)[number];

/** PATCH /api/settings body: any subset of the editable keys; unknown and server-only keys rejected (400). */
export const SettingsPatchSchema = SettingsSchema.omit({ 'runtime.modelCaps': true }).partial().strict();
export type SettingsPatch = z.infer<typeof SettingsPatchSchema>;
/** Server-side writes through SettingsService.patch (e.g. WP1's diagnostics run writing runtime.modelCaps): every key. */
export const SettingsInternalPatchSchema = SettingsSchema.partial().strict();
export type SettingsInternalPatch = z.infer<typeof SettingsInternalPatchSchema>;

/**
 * Guard settings (§7.4): a change needs step-up U:konfiguration on PATCH /api/settings; the light import never applies
 * them and reports them as "nur in der App mit Face ID änderbar" (§20). `push.sperrbildschirm` counts only for neutral → kurz, `support.auto_antworten` only for off → on.
 */
export const GUARD_SETTINGS = ['notion.pages', 'notion.intern_heading', 'shopify.doppelname_hold_hinweis', 'push.sperrbildschirm', 'support.auto_antworten'] as const satisfies readonly SettingsKey[];

/** The guard settings this patch actually changes against `current` (deep compare; an unchanged value is no change). */
export function guardSettingsTouched(current: Settings, patch: Partial<Settings>): SettingsKey[] {
  const out: SettingsKey[] = [];
  for (const key of GUARD_SETTINGS) {
    if (!Object.prototype.hasOwnProperty.call(patch, key) || patch[key] === undefined) continue;
    if (key === 'push.sperrbildschirm') {
      if (current[key] === 'neutral' && patch[key] === 'kurz') out.push(key);
      continue;
    }
    if (key === 'support.auto_antworten') {
      if (current[key] !== true && patch[key] === true) out.push(key);   // switching it off needs no step-up
      continue;
    }
    if (!sameValue(current[key], patch[key])) out.push(key);
  }
  return out;
}

function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  const ka = Object.keys(a).sort();
  const kb = Object.keys(b).sort();
  if (ka.length !== kb.length || ka.some((k, i) => k !== kb[i])) return false;
  return ka.every((k) => sameValue((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}

/** "Stunden gespart" minutes per successful tool run (architecture §17.3; assumptions, not measurements). */
export const DEFAULT_MINUTES_PER_ACTION: Record<string, number> = {
  gmail_send_reply: 6, gmail_create_draft: 5, gmail_set_labels: 0.5, gmail_archive: 0.5, gmail_spam: 0.5,
  shopify_order_note_append: 1, shopify_order_address_update: 3, shopify_order_email_update: 2, shopify_customer_update: 2,
  shopify_fulfillment_hold: 2, shopify_fulfillment_release: 2, shopify_order_edit: 8, shopify_invoice_send: 5,
  shopify_draft_order_invoice: 6, stz_app__retourenlabel_erstellen: 4, retoure_pruefen: 2, report_create: 15,
  task_complete: 5, notion_append: 2,
};

/** §7.4 defaults; raised by the diagnostics run, lowered by a production 400 (§10.5). */
export const DEFAULT_MODEL_CAPS: Record<ModelId, ModelCaps> = {
  'claude-opus-5-5': { systemMessages: true, perMessageEffort: false },
  'claude-sonnet-5-5': { systemMessages: false, perMessageEffort: false },
  'claude-fable-5-1': { systemMessages: true, perMessageEffort: false },
  'claude-haiku-4-5': { systemMessages: false, perMessageEffort: false },
};

export const SETTINGS_DEFAULTS: Settings = {
  'profile.text': '',
  'address.mode': 'mix',
  'answer.length': 'normal',
  'ui.theme': 'auto',
  'ui.accent': 'silber',
  'models.default_agent': 'claude-opus-5-5',
  'models.voice': 'claude-sonnet-5-5',
  'models.voice_effort': 'low',
  'models.utility': 'claude-haiku-4-5',
  'budget.monthly_cap_eur': 100,
  'budget.warn_ratio': 0.8,
  'budget.hard_stop': true,
  // DECISION (§7.4): deliberately conservative placeholder until the first ECB rate arrives; not a market rate.
  'budget.eur_per_usd_fallback': 1.0,
  'budget.elevenlabs_eur_per_1k_chars': null, // UNVERIFIED price (§26 U4)
  'voice.default_voice_id': null,
  'voice.tts_model': 'eleven_flash_v2_5',
  'voice.speed': 1.0,
  'voice.stability': 0.6,
  'voice.similarity': 0.8,
  'voice.auto_read': true,
  'voice.speak_new': true,
  'voice.wake': 'aus', // UNVERIFIED meaning (§26 U5)
  'voice.basis': { voiceURI: '', voiceName: '', rate: 1, pitch: 1 },
  'tones.normal': 'glas',
  'tones.urgent': 'klingel',
  'push.normal': true,
  'push.dringend': true,
  'push.sperrbildschirm': 'kurz',
  'support.auto_antworten': false,
  'calls.enabled': true,
  'calls.quiet_hours': null,
  'calls.provider': 'push',
  'meetings.rounds': 1,
  'cockpit.low_stock_threshold': 5,
  'cockpit.minutes_per_action': DEFAULT_MINUTES_PER_ACTION,
  'notion.pages': {
    analyst_wissen: '3eb78205-d4af-8184-bdaa-daa0b1d823ee',
    nachschub: '3eb78205-d4af-81d2-95c2-fea0a8be0a90',
    produktionskalender: '3eb78205-d4af-8145-96c4-c419b667e21a',
    infomaterial: '2f978205-d4af-8055-bcf5-e809c926b141',
  },
  'notion.intern_heading': '🔒 Intern',
  'shopify.doppelname_hold_hinweis': 'Doppelter Name', // UNVERIFIED (§26)
  'runtime.compact_at_tokens': 150_000,
  'runtime.modelCaps': DEFAULT_MODEL_CAPS,
  'runtime.features': { serverFallback: true, thinkingUpdates: true, blockBinding: true, perMessageEffort: true },
};

export const SETTINGS_KEYS = Object.keys(SETTINGS_DEFAULTS) as SettingsKey[];

export function isSettingsKey(k: string): k is SettingsKey {
  return Object.prototype.hasOwnProperty.call(SETTINGS_DEFAULTS, k);
}
